/**
 * Lot 2.1 — Pilotage de l'organisme (administrateur). Lecture seule : tout est calculé par le noyau
 * (`domaine/pilotage/agregation.ts`), l'écran met en forme. Chaque graphique a son tableau de données.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Accessibility, BadgeEuro, FileCheck2, FolderKanban, Percent, ShieldAlert } from "lucide-react";
import { api, dateFr, euros, type VuePilotage } from "../api";
import { Alerte, Carte, Chargement, cx, Indicateur, TitrePage } from "../ui/base";
import { Tableau } from "./Divers";
import { AlerteCertificat } from "./QualiopiVitrine";

const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const eurosRonds = (centimes: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(centimes / 100);

export function Pilotage() {
  const [annee, setAnnee] = useState<number | undefined>();
  const vue = useQuery({ queryKey: ["pilotage", annee], queryFn: () => api.get<VuePilotage>(`/admin/pilotage${annee ? `?annee=${annee}` : ""}`) });
  if (vue.isPending) return <Chargement />;
  if (vue.error) return <Alerte ton="danger">{vue.error.message}</Alerte>;
  const { pilotage: p, annees, handicap, referent_handicap: ref, certificat_qualiopi: cq } = vue.data;
  const t = p.totaux;
  const q = p.qualiopi;
  const alertes27 = q.attestations_echues.length + q.attestations_a_renouveler.length;

  return (
    <>
      <TitrePage
        titre="Pilotage"
        soustitre="Le chiffre d'affaires facturé par l'organisme, la commission de portage et la traçabilité Qualiopi. Calculé à partir des dossiers et de leurs factures, jamais saisi."
        actions={
          annees.length > 1 && (
            <select aria-label="Année" value={p.annee} onChange={(e) => setAnnee(Number(e.target.value))} className="chiffres h-10 rounded-sm border border-trait-fort bg-carte px-3 text-sm">
              {annees.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          )
        }
      />

      {cq.etat !== "valide" && (
        <div className="mb-6">
          <AlerteCertificat etat={cq.etat} jours={cq.jours_restants} dateFin={cq.date_fin} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Indicateur libelle={`CA facturé ${p.annee}`} valeur={eurosRonds(t.ca_porte)} detail={`${t.dossiers_factures} dossier(s) facturé(s), par mois de facturation`} icone={<BadgeEuro className="size-4" aria-hidden />} ton="accent" />
        <Indicateur libelle="Commission de portage" valeur={eurosRonds(t.commission)} detail={t.taux_commission === null ? "aucun dossier chiffré" : `soit ${String(t.taux_commission).replace(".", ",")} % du CA facturé`} icone={<Percent className="size-4" aria-hidden />} />
        <Indicateur libelle="Dossiers en cours" valeur={t.dossiers_en_cours} detail={t.dossiers_refuses ? `${t.dossiers_refuses} refus de financement à part` : "validés, pas encore archivés"} icone={<FolderKanban className="size-4" aria-hidden />} vers="/" />
        <Indicateur libelle="Pièces retournées" valeur={q.taux_pieces === null ? "—" : `${q.taux_pieces} %`} detail={`${q.pieces_validees} / ${q.pieces_suivies} pièces signées ou déposées`} icone={<FileCheck2 className="size-4" aria-hidden />} ton={q.taux_pieces !== null && q.taux_pieces < 50 ? "attente" : "neutre"} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <GraphiqueMois p={p} />
        <Carte className={cx("p-5", alertes27 > 0 && "border-attente/60")}>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <ShieldAlert className="size-4 text-encre-3" aria-hidden /> Sous-traitants — indicateur 27
          </h2>
          <p className="mt-0.5 text-[13px] text-encre-2">Attestations des formateurs (URSSAF, assurance, K-bis) échues ou à renouveler dans les 30 jours, d'après les dates saisies sur leur profil.</p>
          {alertes27 === 0 ? (
            <p className="mt-3 text-sm text-encre-2">Aucune attestation échue ni à renouveler.</p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm">
              {[...q.attestations_echues.map((e) => ({ ...e, echue: true })), ...q.attestations_a_renouveler.map((e) => ({ ...e, echue: false }))].map((e) => (
                <li key={`${e.formateur_id}-${e.piece}-${e.expire_le}`} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 border-b border-trait pb-2 last:border-0">
                  <span className="min-w-0">
                    <span className="font-medium">{e.formateur_nom}</span>
                    <span className="block truncate text-[13px] text-encre-3">{e.piece}</span>
                  </span>
                  <span className={cx("chiffres shrink-0 text-[13px] font-semibold", e.echue ? "text-danger-encre" : "text-attente-encre")}>
                    {e.echue ? "échue le" : "expire le"} {dateFr(e.expire_le)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-[13px] text-encre-3">
            Les justificatifs se consultent dans <Link to="/admin/candidatures" className="font-medium text-accent hover:underline">Candidatures</Link>.
          </p>
        </Carte>
      </div>

      <Carte className="mt-6 p-5">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Accessibility className="size-4 text-encre-3" aria-hidden /> Handicap — indicateur 26
        </h2>
        <p className="mt-0.5 text-[13px] text-encre-2">
          {ref.nom ? (
            <>Référent handicap : <strong className="text-encre">{ref.nom}</strong>{ref.email && ` · ${ref.email}`}{ref.telephone && ` · ${ref.telephone}`}.</>
          ) : (
            <>Aucun référent handicap renseigné : <Link to="/admin/organisme" className="font-medium text-accent hover:underline">le désigner dans Organisme</Link> (il est cité dans chaque convocation).</>
          )}
        </p>
        {handicap.length === 0 ? (
          <p className="mt-3 text-sm text-encre-2">Aucun apprenant d'un dossier en cours n'a signalé de situation de handicap.</p>
        ) : (
          <ul className="mt-3 divide-y divide-trait text-sm">
            {/* Un apprenant inscrit à plusieurs dossiers n'apparaît qu'une fois, avec chacun de ses dossiers. */}
            {[...new Set(handicap.map((h) => h.apprenant))].map((apprenant) => {
              const lignes = handicap.filter((h) => h.apprenant === apprenant);
              return (
                <li key={apprenant} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-2">
                  <span className="min-w-0">
                    <span className="font-medium">{apprenant}</span>
                    <span className="block text-[13px] text-encre-2">Besoin exprimé : {lignes[0]!.besoin}</span>
                  </span>
                  <span className="flex flex-col items-end gap-0.5">
                    {lignes.map((h) => (
                      <Link key={h.dossier_id} to="/dossiers/$id" params={{ id: h.dossier_id }} className="chiffres shrink-0 text-[13px] font-medium text-accent hover:underline">
                        {h.dossier_reference} · {h.formation_date_debut ? dateFr(h.formation_date_debut) : "dates à définir"}
                      </Link>
                    ))}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Carte>

      <div className="mt-6">
        <Tableau titre="Où en sont les dossiers aujourd'hui" entetes={["Étape", "Dossiers", "Montant HT"]} lignes={p.par_etape.map((e) => [`${e.etape} · ${e.titre}`, e.dossiers, euros(e.montant_ht)])} />
      </div>
    </>
  );
}

/** Barres mensuelles du CA facturé (mois de la facture de l'OF), commission en surimpression ; le tableau qui suit porte les mêmes chiffres. */
function GraphiqueMois({ p }: { p: VuePilotage["pilotage"] }) {
  const max = Math.max(1, ...p.mois.map((m) => m.ca_porte));
  const vide = p.mois.every((m) => m.ca_porte === 0);
  return (
    <Carte className="p-5">
      <h2 className="text-base font-semibold">CA facturé et commission par mois de facturation — {p.annee}</h2>
      <p className="mt-0.5 text-[13px] text-encre-2">
        <span className="mr-3 inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm bg-accent/30" aria-hidden />CA facturé HT</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm bg-accent" aria-hidden />dont commission</span>
      </p>
      <div className="mt-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-md border border-trait bg-papier-2 px-3 py-2 text-sm">
        <span className="text-encre-2">Validé, non encore facturé <span className="text-[12.5px] text-encre-3">(à date, {p.non_facture.dossiers} dossier{p.non_facture.dossiers > 1 ? "s" : ""})</span></span>
        <span className="chiffres font-semibold">{euros(p.non_facture.montant_ht)} HT</span>
      </div>
      {vide ? (
        <p className="mt-4 text-sm text-encre-2">Aucune facture de l'organisme émise en {p.annee}.</p>
      ) : (
        <div className="mt-4 overflow-x-auto" role="img" aria-label={`Histogramme du CA facturé par mois de facturation en ${p.annee} ; les valeurs figurent dans le tableau ci-dessous.`}>
          <div className="grid h-44 min-w-[480px] grid-cols-12 items-end gap-1.5 border-b border-trait">
            {p.mois.map((m, i) => (
              <div key={m.mois} className="relative flex h-full items-end" title={`${MOIS[i]} : ${euros(m.ca_porte)} (commission ${euros(m.commission)})`}>
                <div className="relative w-full rounded-t-sm bg-accent/30" style={{ height: `${(m.ca_porte / max) * 100}%` }}>
                  <div className="absolute inset-x-0 bottom-0 rounded-t-sm bg-accent" style={{ height: m.ca_porte ? `${(m.commission / m.ca_porte) * 100}%` : 0 }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-1 grid min-w-[480px] grid-cols-12 gap-1.5 text-center text-[11px] text-encre-3">
            {MOIS.map((m) => <span key={m}>{m}</span>)}
          </div>
        </div>
      )}
      <details className="mt-4 text-sm">
        <summary className="cursor-pointer font-medium text-accent">Voir les chiffres mois par mois</summary>
        <table className="chiffres mt-2 w-full text-[13px]">
          <thead>
            <tr className="text-left text-encre-3"><th className="py-1 font-medium">Mois</th><th className="py-1 text-right font-medium">Dossiers</th><th className="py-1 text-right font-medium">CA facturé</th><th className="py-1 text-right font-medium">Commission</th></tr>
          </thead>
          <tbody className="divide-y divide-trait">
            {p.mois.map((m, i) => (
              <tr key={m.mois}><td className="py-1">{MOIS[i]}</td><td className="py-1 text-right">{m.dossiers}</td><td className="py-1 text-right">{euros(m.ca_porte)}</td><td className="py-1 text-right">{euros(m.commission)}</td></tr>
            ))}
          </tbody>
        </table>
      </details>
      <p className="mt-3 text-[12.5px] text-encre-3">
        <strong className="font-semibold text-encre-2">Méthode de calcul</strong> : un dossier entre dans le CA du mois où l'organisme émet sa facture (pièce « Facture de l'OF », créée à la demande de paiement), pour son prix de vente HT ; les refus de financement sont exclus. Un dossier validé mais pas encore facturé n'entre dans aucun mois : il est compté à part, ci-dessus. Commission = prix de vente HT − montant reversé au formateur, au taux fixe de l'organisme (25 % par défaut, réglé dans « Organisme »).
      </p>
    </Carte>
  );
}
